import React, { FC } from "react";
import styles from "./IndexHero.module.scss";

/**
 * sez.4.2 Layout B "Indice" — testa della pagina per outputType='index'.
 * Mostra il VALORE PRIMARIO grande (VMA/VIFT/DSI/asimmetria) + sotto-valori
 * secondari (HRmax, VO2max, strongSide, asymmetryPct) quando presenti.
 */

/** Mappa chiave output → metadati per Layout B (sez.4.2, spec Profiling) */
export const INDEX_PRIMARY_META: Record<
  string,
  { label: string; unit: string; isPrimary: boolean }
> = {
  // Protocolli endurance (P10 VAMEVAL, P11 Gacon, P12/P13 Yo-Yo, P14 30-15 IFT)
  VMA: { label: "VMA", unit: "km/h", isPrimary: true },
  VIFT: { label: "VIFT", unit: "km/h", isPrimary: true },
  VO2max: { label: "VO₂max stimato", unit: "ml/kg/min", isPrimary: false },
  HRmax: { label: "FC max", unit: "bpm", isPrimary: false },
  distance: { label: "Distanza", unit: "m", isPrimary: false },
  // Protocolli forza (P7 IMTP+DSI, P8 Nordic)
  DSI: { label: "DSI", unit: "", isPrimary: true },
  peakForce: { label: "Picco forza", unit: "N", isPrimary: false },
  asymmetryPct: { label: "Asimmetria", unit: "%", isPrimary: false },
  strongSide: { label: "Lato forte", unit: "", isPrimary: false },
  eccentricForce: { label: "Forza ecc.", unit: "N", isPrimary: false },
};

/** Tenta di individuare il campo primario dell'output (ordine di priorità) */
const PRIMARY_KEYS_ORDER = ["VMA", "VIFT", "DSI", "asymmetryPct"];

function detectPrimary(
  outputs: Record<string, any>
): { key: string; value: number; label: string; unit: string } | null {
  for (const key of PRIMARY_KEYS_ORDER) {
    if (outputs[key] != null && typeof outputs[key] === "number") {
      const meta = INDEX_PRIMARY_META[key] ?? { label: key, unit: "" };
      return { key, value: outputs[key] as number, label: meta.label, unit: meta.unit };
    }
  }
  // Fallback: primo valore numerico disponibile
  for (const [key, val] of Object.entries(outputs)) {
    if (typeof val === "number") {
      const meta = INDEX_PRIMARY_META[key] ?? { label: key.toUpperCase(), unit: "" };
      return { key, value: val, label: meta.label, unit: meta.unit };
    }
  }
  return null;
}

function formatPrimary(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
}

function formatSecondary(v: any): string {
  if (typeof v === "number") {
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(2);
  }
  return String(v);
}

interface IIndexHeroProps {
  outputs: Record<string, any>;
}

export const IndexHero: FC<IIndexHeroProps> = ({ outputs }) => {
  const primary = detectPrimary(outputs);
  if (!primary) return null;

  // Valori secondari: tutti i campi rimanenti con metadati, escluso il primario
  const secondaryEntries = Object.entries(outputs)
    .filter(([key, val]) => {
      if (key === primary.key) return false;
      if (val == null) return false;
      const meta = INDEX_PRIMARY_META[key];
      // Mostra solo i campi con metadati noti, o stringhe/numeri generici
      return (
        meta != null ||
        typeof val === "number" ||
        typeof val === "string"
      );
    })
    .slice(0, 4); // max 4 sotto-valori per non sovraccaricare

  return (
    <div className={styles.heroWrapper}>
      {/* Valore primario grande (sez.4.2) */}
      <div className={styles.primaryBlock}>
        <p className={styles.primaryLabel}>{primary.label}</p>
        <div className={styles.primaryValueRow}>
          <span className={styles.primaryValue}>{formatPrimary(primary.value)}</span>
          {primary.unit && (
            <span className={styles.primaryUnit}>{primary.unit}</span>
          )}
        </div>
      </div>

      {/* Sotto-valori secondari (sez.4.2) */}
      {secondaryEntries.length > 0 && (
        <div className={styles.secondaryGrid}>
          {secondaryEntries.map(([key, val]) => {
            const meta = INDEX_PRIMARY_META[key] ?? {
              label: key.toUpperCase(),
              unit: "",
            };
            return (
              <div key={key} className={styles.secondaryCard}>
                <p className={styles.secondaryLabel}>{meta.label}</p>
                <p className={styles.secondaryValue}>
                  {formatSecondary(val)}
                  {meta.unit && (
                    <span className={styles.secondaryUnit}> {meta.unit}</span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IndexHero;
